import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api, definirTokenAcesso, ErroApi } from '@/lib/api';
import type { RespostaLogin, UsuarioAutenticado } from './tipos';
import { ContextoAuthReact, type ContextoAuth } from './contexto';

/**
 * Renova o token um pouco antes de ele expirar. A margem evita a corrida em
 * que a requisição sai com um token que expira no caminho.
 */
const MARGEM_RENOVACAO_SEGUNDOS = 60;

export function ProvedorAuth({ children }: { children: React.ReactNode }) {
  const [usuario, setUsuario] = React.useState<UsuarioAutenticado | null>(null);
  const [carregando, setCarregando] = React.useState(true);
  const queryClient = useQueryClient();
  const temporizador = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const limparTemporizador = React.useCallback(() => {
    if (temporizador.current) {
      clearTimeout(temporizador.current);
      temporizador.current = null;
    }
  }, []);

  const encerrarSessaoLocal = React.useCallback(() => {
    limparTemporizador();
    definirTokenAcesso(null);
    setUsuario(null);
    // Descarta dados de outro usuário do cache — em um sistema com isolamento
    // por UBS, deixá-los seria vazamento entre sessões no mesmo navegador.
    queryClient.clear();
  }, [limparTemporizador, queryClient]);

  // `aplicarSessao` agenda a próxima renovação e `renovar` chama
  // `aplicarSessao` — uma referência circular. A ref quebra o ciclo sem
  // depender da ordem de avaliação dos closures.
  const renovarRef = React.useRef<() => Promise<boolean>>(() => Promise.resolve(false));

  const aplicarSessao = React.useCallback(
    (resposta: RespostaLogin) => {
      definirTokenAcesso(resposta.accessToken);
      setUsuario(resposta.usuario);

      limparTemporizador();
      const atraso = Math.max(resposta.expiraEmSegundos - MARGEM_RENOVACAO_SEGUNDOS, 30) * 1000;
      temporizador.current = setTimeout(() => {
        void renovarRef.current();
      }, atraso);
    },
    [limparTemporizador],
  );

  /**
   * Renovação em voo, compartilhada por todos os chamadores.
   *
   * O refresh token é rotacionado a cada uso: duas chamadas simultâneas fazem
   * a primeira invalidar o cookie que a segunda ainda está apresentando, e a
   * segunda recebe 401 e encerra a sessão — mesmo tendo a primeira renovado
   * com sucesso. Acontecia a cada F5, porque o StrictMode executa o efeito de
   * montagem duas vezes. Compartilhar a promessa resolve dentro da aba; entre
   * abas não há como coordenar, e cada uma renova a sua.
   */
  const renovacaoEmVoo = React.useRef<Promise<boolean> | null>(null);

  const renovar = React.useCallback(async (): Promise<boolean> => {
    renovacaoEmVoo.current ??= api
      .post<RespostaLogin>('/auth/refresh')
      .then((resposta) => {
        aplicarSessao(resposta);
        return true;
      })
      .catch((erro: unknown) => {
        // Encerra apenas quando o servidor de fato recusou a credencial.
        // Um 429, um 5xx ou uma queda de rede não são "sessão inválida":
        // tratá-los como tal joga para a tela de login quem só recarregou a
        // página numa hora ruim, e o usuário perde o que estava preenchendo.
        const credencialRecusada =
          erro instanceof ErroApi && (erro.status === 401 || erro.status === 403);

        if (credencialRecusada) encerrarSessaoLocal();
        return false;
      })
      .finally(() => {
        renovacaoEmVoo.current = null;
      });

    return renovacaoEmVoo.current;
  }, [aplicarSessao, encerrarSessaoLocal]);

  renovarRef.current = renovar;

  // Ao abrir a aplicação, tenta restaurar a sessão pelo cookie httpOnly.
  // O token de acesso vive só em memória, então recarregar a página sempre
  // exige esta etapa.
  React.useEffect(() => {
    void renovarRef.current().finally(() => setCarregando(false));
    return limparTemporizador;
  }, [limparTemporizador]);

  const entrar = React.useCallback(
    async (email: string, senha: string): Promise<UsuarioAutenticado> => {
      const resposta = await api.post<RespostaLogin>('/auth/login', { email, senha });
      aplicarSessao(resposta);
      return resposta.usuario;
    },
    [aplicarSessao],
  );

  const sair = React.useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (erro) {
      // Falha ao avisar o servidor não pode impedir a saída local: o usuário
      // pode estar num terminal compartilhado.
      if (!(erro instanceof ErroApi)) throw erro;
    } finally {
      encerrarSessaoLocal();
    }
  }, [encerrarSessaoLocal]);

  const atualizarUsuario = React.useCallback((dados: Partial<UsuarioAutenticado>) => {
    setUsuario((atual) => (atual ? { ...atual, ...dados } : atual));
  }, []);

  const valor = React.useMemo<ContextoAuth>(
    () => ({ usuario, carregando, entrar, sair, atualizarUsuario }),
    [usuario, carregando, entrar, sair, atualizarUsuario],
  );

  return <ContextoAuthReact.Provider value={valor}>{children}</ContextoAuthReact.Provider>;
}
